/**
 * Route-aware help content for the contextual help panel.
 * Matched by pathname prefix. Returns { title, sections } or null.
 */

const HELP_CONTENT_EN = [
    {
        match: (p) => p === '/dashboard',
        title: 'Dashboard',
        sections: [
            {
                heading: 'Overview',
                text: 'Your personal overview. Overdue indicator appears when you have months with no submissions past the reporting deadline.',
            },
            {
                heading: 'Quick actions',
                text: 'Click Reports in the sidebar to log time and expenses for the current month.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/log-reports'),
        title: 'Timesheet & Reports',
        sections: [
            {
                heading: 'Logging time',
                text: 'Click any calendar day to open the entry panel. Select the project, enter hours, and save. Internal project hours above your daily base (8h) are automatically banked as flex when your timesheet is approved.',
            },
            {
                heading: 'Logging expenses',
                text: 'From the same day panel, switch to the Mileage, Receipt, or Allowance tab. Receipts in foreign currencies are automatically converted to SEK using Riksbanken\'s rate.',
            },
            {
                heading: 'Flex Bank vs Current Flex',
                text: 'Flex Bank (top right) shows your total approved flex balance across all months. Current Flex shows the projected impact of this month\'s draft entries — it updates as you log hours.',
            },
            {
                heading: 'Colours',
                text: 'Green pill or green TrendingUp = flex will be earned. Blue pill = billable overtime (OT 1.5×/2×). Red pill = red-day overtime on a billable project. Dark pill = regular hours.',
            },
            {
                heading: 'Bulk mode',
                text: 'Click Bulk Add to select multiple days at once — useful for logging the same project across a whole week or month in one step. Click individual day cells to select, then Add to N Days.',
            },
            {
                heading: 'Submitting',
                text: 'Lock each week when done, then lock the month, then click Submit Timesheet. Submit Expenses separately. Both must be submitted for admin approval.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/vacation-planner'),
        title: 'Vacation Planner',
        sections: [
            {
                heading: 'Planning leave',
                text: 'Select dates and submit a vacation plan. Your manager reviews and approves it. Approved plans deduct from your vacation balance.',
            },
            {
                heading: 'Vacation balance',
                text: 'Paid vacation days are shown on your Profile page. Advance vacation can be used if you have none remaining.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/profile'),
        title: 'Profile',
        sections: [
            {
                heading: 'Flex balance',
                text: 'Your accumulated approved flex hours across all months. Use flex by logging a Flex deviation on any working day — it deducts from this balance.',
            },
            {
                heading: 'Vacation days',
                text: 'Your remaining paid vacation entitlement for this year.',
            },
            {
                heading: 'Transaction history',
                text: 'All flex deposits (earned) and withdrawals (used) listed with dates and amounts.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/approvals'),
        title: 'Timesheet Approvals',
        sections: [
            {
                heading: 'Reviewing a timesheet',
                text: 'Click a row to see the employee\'s time entries for the month. Use Approve to confirm and bank their flex hours, or Reject with a reason to send it back for corrections.',
            },
            {
                heading: 'Effect of approval',
                text: 'Approving runs the flex calculation — extra hours above base on internal projects are deposited to the employee\'s flex bank automatically.',
            },
            {
                heading: 'Reopen requests',
                text: 'If an employee needs to correct a submitted or approved timesheet they can request a reopen. You will see the request on your Admin Dashboard — approve it to unlock editing, or reject it to keep the current state.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/expense-approvals'),
        title: 'Expense Approvals',
        sections: [
            {
                heading: 'Reviewing expenses',
                text: 'Check the employee\'s mileage, receipts, and allowances. Verify amounts and attached receipts. Approve to confirm reimbursement, or reject with a reason.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/reports'),
        title: 'Salaries',
        sections: [
            {
                heading: 'Salary report',
                text: 'Shows all approved time entries for the selected month: hours by employee, OT buckets, flex earned/used, expenses.',
            },
            {
                heading: 'Sync to Fortnox',
                text: 'If Fortnox is connected, use Sync to Fortnox to push salary transactions, absences, expenses, billing invoices, and receipt vouchers. Always run a dry-run preview first to catch mapping errors.',
            },
            {
                heading: 'Export and sending',
                text: 'Downloads and the email to your accountant live under Monthly Reports.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/monthly-reports'),
        title: 'Monthly Reports',
        sections: [
            {
                heading: 'One send per month',
                text: 'Collects each month’s customer invoices (PDF copies), salaries, expenses, cost centres (revenue vs costs) and supplier invoices. Pick the month and send everything to your accountant in one email.',
            },
            {
                heading: 'Downloads',
                text: 'Each document can also be downloaded on its own as Excel or ZIP.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/billing'),
        title: 'Billing',
        sections: [
            {
                heading: 'Billable hours',
                text: 'Shows approved time on client projects by employee and project. Rates come from project settings.',
            },
            {
                heading: 'Export',
                text: 'Downloads and the email to your accountant live under Monthly Reports.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/vacation-calendar'),
        title: 'Vacation Calendar',
        sections: [
            {
                heading: 'Team overview',
                text: 'See all pending, approved, and rejected vacation plans across your team. Approve or reject requests from here.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/settings'),
        title: 'Admin Settings',
        sections: [
            {
                heading: 'Employees',
                text: 'Add, edit, or deactivate employees. Set monthly salary, daily base hours override, and vacation entitlement.',
            },
            {
                heading: 'Projects',
                text: 'Create and manage projects. Mark projects as billable for invoice tracking. Set budgets and assign activities.',
            },
            {
                heading: 'Payroll Settings',
                text: 'Configure the company-wide daily base hours (default 8h) and employer tax rate (arbetsgivaravgift, default 31.42%) used in salary cost calculations.',
            },
            {
                heading: 'Fortnox',
                text: 'Connect to Fortnox for salary, absence, expense, and billing sync. Configure salary codes, absence codes, GL accounts, and customer mappings.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/admin'),
        title: 'Admin Dashboard',
        sections: [
            {
                heading: 'Team submission status',
                text: 'The table shows every active employee\'s timesheet status for the selected month. Hours Logged shows how many hours they have recorded so far against the month norm. Flex shows their current flex bank balance.',
            },
            {
                heading: 'Pending approvals',
                text: 'Timesheets and expense reports waiting for your action are shown as quick-link buttons. The number updates in real time as you approve or reject.',
            },
            {
                heading: 'Budget warnings',
                text: 'Projects at 90%+ of their budget appear at the top as amber/red alerts. Click through to the Projects page to see full cost breakdown.',
            },
            {
                heading: 'Reopen requests',
                text: 'When an employee requests to edit a locked timesheet, the request appears here. Approve to unlock editing; reject to keep the submitted state.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/analytics'),
        title: 'Analytics',
        sections: [
            {
                heading: 'Filters',
                text: 'Select a year and month range to narrow the view. Filter further by a specific project or employee. Click Apply to refresh the charts.',
            },
            {
                heading: 'Hours by project',
                text: 'Shows total approved hours logged against each project. The budget vs actual table below highlights projects where employer cost is approaching or exceeding budget.',
            },
            {
                heading: 'Monthly trend',
                text: 'Bar chart of hours logged per month, with a line showing cumulative labour cost in SEK. Useful for spotting seasonal patterns or months with unusually high output.',
            },
            {
                heading: 'Expense breakdown',
                text: 'Pie chart of approved expenses split by type (mileage, receipts, allowance). The stacked bar chart below shows the monthly spend per category.',
            },
            {
                heading: 'Data completeness',
                text: 'Only approved time entries and approved expenses are included. Draft and submitted-but-not-yet-approved entries are excluded to keep the financials accurate.',
            },
        ],
    },
    {
        // Pay slips live on the Profile page (see ProfilePage.jsx — payslips list).
        // There is no separate /dashboard/records route — kept distinct match so it
        // doesn't shadow the broader /dashboard/profile help entry below.
        match: (p) => p.startsWith('/dashboard/profile'),
        title: 'Profile & pay slips',
        sections: [
            {
                heading: 'Pay slips',
                text: 'Pay slips are generated automatically when your timesheet is approved each month. Download any month\'s file from the list on this page.',
            },
            {
                heading: 'Pay slip history',
                text: 'The full list shows all months for which a pay slip has been generated. Slips are stored securely and remain available regardless of employment status.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/integrations'),
        title: 'Integrations',
        sections: [
            {
                heading: 'Fortnox',
                text: 'Connect your Fortnox account to sync salary transactions, absences, expenses, and billing invoices. Configure salary codes, absence codes, and customer mappings in Admin Settings → Fortnox.',
            },
            {
                heading: 'Email (Brevo)',
                text: 'Monthly reminder emails and report delivery are sent via Brevo. Configure the API key in Admin Settings → Notifications.',
            },
            {
                heading: 'Microsoft 365',
                text: 'SSO via Microsoft 365 allows employees to sign in with their company account. Credentials are configured at the platform level — contact your administrator.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/company'),
        title: 'Company Settings',
        sections: [
            {
                heading: 'Company profile',
                text: 'Legal address, VAT number, phone, and website. Company name and organisation number are set at onboarding and cannot be changed here.',
            },
            {
                heading: 'Branding',
                text: 'Upload your company logo (shown in the top bar) and a login background image. PWA icons for home-screen installation can also be uploaded here.',
            },
            {
                heading: 'Auth & Access',
                text: 'Enable or disable email/password login and Microsoft 365 SSO independently. You can restrict login to specific email domains.',
            },
        ],
    },
];

const HELP_CONTENT_SV = [
    {
        match: (p) => p === '/dashboard',
        title: 'Översikt',
        sections: [
            {
                heading: 'Översikt',
                text: 'Din personliga översikt. En förfallen-indikator visas när du har månader utan inlämningar efter rapporteringsdeadlinen.',
            },
            {
                heading: 'Snabbåtgärder',
                text: 'Klicka på Rapporter i sidopanelen för att logga tid och utlägg för aktuell månad.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/log-reports'),
        title: 'Tidrapport och rapporter',
        sections: [
            {
                heading: 'Logga tid',
                text: 'Klicka på valfri kalenderdag för att öppna inmatningspanelen. Välj projekt, ange timmar och spara. Interna projekttimmar över din dagliga bas (8h) bankas automatiskt som flex när din tidrapport godkänns.',
            },
            {
                heading: 'Logga utlägg',
                text: 'Från samma dagspanel, byt till fliken Mil, Kvitto eller Traktamente. Kvitton i utländsk valuta konverteras automatiskt till SEK med Riksbankens kurs.',
            },
            {
                heading: 'Flexsaldo vs Aktuell flex',
                text: 'Flexsaldo (övre höger) visar ditt totala godkända flexsaldo för alla månader. Aktuell flex visar den beräknade effekten av den aktuella månadens utkast — uppdateras när du loggar timmar.',
            },
            {
                heading: 'Färger',
                text: 'Grön piller eller grön TrendingUp = flex kommer att tjänas in. Blå piller = fakturerbar övertid (OT 1,5×/2×). Röd piller = röddag-övertid på fakturerbart projekt. Mörk piller = vanliga timmar.',
            },
            {
                heading: 'Massredigering',
                text: 'Klicka på Masslägg till för att välja flera dagar på en gång — praktiskt för att logga samma projekt för en hel vecka eller månad i ett steg. Klicka på enskilda dagar och välj sedan Lägg till i N dagar.',
            },
            {
                heading: 'Skicka in',
                text: 'Lås varje vecka när du är klar, lås sedan månaden och klicka Skicka tidrapport. Skicka utläggsrapporten separat. Båda måste skickas för administratörsgodkännande.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/vacation-planner'),
        title: 'Semesterplanering',
        sections: [
            {
                heading: 'Planera ledighet',
                text: 'Välj datum och skicka in en semesterplan. Din chef granskar och godkänner den. Godkända planer dras av från ditt semestersaldo.',
            },
            {
                heading: 'Semestersaldo',
                text: 'Betalda semesterdagar visas på din profilsida. Förskottssemester kan användas om du inte har några kvar.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/profile'),
        title: 'Profil',
        sections: [
            {
                heading: 'Flexsaldo',
                text: 'Dina ackumulerade godkända flextimmar för alla månader. Använd flex genom att logga en Flex-avvikelse på en arbetsdag — det dras av från detta saldo.',
            },
            {
                heading: 'Semesterdagar',
                text: 'Ditt återstående betalda semesterrättighet för detta år.',
            },
            {
                heading: 'Transaktionshistorik',
                text: 'Alla flexinsättningar (intjänade) och uttag (använda) listade med datum och belopp.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/approvals'),
        title: 'Tidrapportsgodkännanden',
        sections: [
            {
                heading: 'Granska en tidrapport',
                text: 'Klicka på en rad för att se medarbetarens tidsposter för månaden. Använd Godkänn för att bekräfta och banka deras flextimmar, eller Avvisa med en anledning för att skicka tillbaka för korrigering.',
            },
            {
                heading: 'Effekt av godkännande',
                text: 'Vid godkännande körs flexberäkningen — extra timmar över basen på interna projekt sätts automatiskt in på medarbetarens flexkonto.',
            },
            {
                heading: 'Återöppningsförfrågningar',
                text: 'Om en anställd behöver korrigera en inlämnad eller godkänd tidrapport kan de begära återöppning. Du ser förfrågan på din Admins instrumentpanel — godkänn för att låsa upp redigering eller avvisa för att behålla nuvarande status.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/expense-approvals'),
        title: 'Utläggsgodkännanden',
        sections: [
            {
                heading: 'Granska utlägg',
                text: 'Kontrollera medarbetarens milersättning, kvitton och traktamenten. Verifiera belopp och bifogade kvitton. Godkänn för att bekräfta ersättning, eller avvisa med en anledning.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/reports'),
        title: 'Löner',
        sections: [
            {
                heading: 'Löneunderlag',
                text: 'Visar alla godkända tidsposter för vald månad: timmar per anställd, övertidsbuckets, intjänad/använd flex, utlägg.',
            },
            {
                heading: 'Synkronisera till Fortnox',
                text: 'Om Fortnox är anslutet, använd Synka till Fortnox för att skicka lönetransaktioner, frånvaro, utlägg, fakturaunderlag och kvittoverifikationer. Kör alltid en förhandsgranskning först för att fånga mappningsfel.',
            },
            {
                heading: 'Export och utskick',
                text: 'Nedladdning och e-post till redovisaren finns under Månadsrapporter.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/monthly-reports'),
        title: 'Månadsrapporter',
        sections: [
            {
                heading: 'Ett utskick per månad',
                text: 'Samlar månadens kundfakturor (PDF-kopior), löner, utlägg, kostnadsställen (intäkter mot kostnader) och leverantörsfakturor. Välj månad och skicka allt till redovisaren i ett e-postmeddelande.',
            },
            {
                heading: 'Nedladdning',
                text: 'Varje underlag kan också laddas ner separat som Excel eller ZIP.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/billing'),
        title: 'Fakturering',
        sections: [
            {
                heading: 'Fakturerbara timmar',
                text: 'Visar godkänd tid på kundprojekt per anställd och projekt. Tariffer kommer från projektinställningar.',
            },
            {
                heading: 'Export',
                text: 'Nedladdning och e-post till redovisaren finns under Månadsrapporter.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/vacation-calendar'),
        title: 'Semesterkalender',
        sections: [
            {
                heading: 'Teamöversikt',
                text: 'Se alla väntande, godkända och avvisade semesterplaner för ditt team. Godkänn eller avvisa förfrågningar härifrån.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/settings'),
        title: 'Admininställningar',
        sections: [
            {
                heading: 'Anställda',
                text: 'Lägg till, redigera eller inaktivera anställda. Ange månadslön, daglig bastimme-override och semesterrättighet.',
            },
            {
                heading: 'Projekt',
                text: 'Skapa och hantera projekt. Markera projekt som fakturerbara för fakturaspårning. Sätt budgetar och tilldela aktiviteter.',
            },
            {
                heading: 'Löneinställningar',
                text: 'Konfigurera företagets dagliga bastimmar (standard 8h) och arbetsgivaravgiften (standard 31,42%) som används i lönekostnadsberäkningar.',
            },
            {
                heading: 'Fortnox',
                text: 'Anslut till Fortnox för löne-, frånvaro-, utlägss- och faktureringssynkronisering. Konfigurera lönekoder, frånvarokoder, redovisningskonton och kundmappningar.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/admin'),
        title: 'Admins instrumentpanel',
        sections: [
            {
                heading: 'Teamets inlämningsstatus',
                text: 'Tabellen visar varje aktiv anställds tidrapportstatus för vald månad. Inloggade timmar visar hur många timmar de loggat hittills mot månadsmassan. Flex visar deras aktuella flexsaldo.',
            },
            {
                heading: 'Väntande godkännanden',
                text: 'Tidrapporter och utläggsrapporter som väntar på din åtgärd visas som snabblänksknappar. Antalet uppdateras i realtid.',
            },
            {
                heading: 'Budgetvarningar',
                text: 'Projekt vid 90%+ av sin budget visas överst som gula/röda varningar. Klicka vidare till Projektsidan för att se fullständig kostnadsuppdelning.',
            },
            {
                heading: 'Återöppningsförfrågningar',
                text: 'När en anställd begär att redigera en låst tidrapport visas förfrågan här. Godkänn för att låsa upp redigering; avvisa för att behålla inlämnat tillstånd.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/analytics'),
        title: 'Analys',
        sections: [
            {
                heading: 'Filter',
                text: 'Välj ett år och månadsintervall för att begränsa vyn. Filtrera vidare på ett specifikt projekt eller en anställd. Klicka Tillämpa för att uppdatera graferna.',
            },
            {
                heading: 'Timmar per projekt',
                text: 'Visar totalt antal godkända timmar loggade mot varje projekt. Budget vs utfall-tabellen nedan markerar projekt där arbetsgivarkostnaden närmar sig eller överstiger budgeten.',
            },
            {
                heading: 'Månadsvis trend',
                text: 'Stapeldiagram med timmar per månad och en linje som visar ackumulerad lönekostnad i SEK. Användbart för att upptäcka säsongsmönster.',
            },
            {
                heading: 'Utläggsfördelning',
                text: 'Cirkeldiagram med godkända utlägg uppdelat på typ (mil, kvitton, traktamente). Det staplade stapeldiagrammet nedan visar månadsutgifter per kategori.',
            },
            {
                heading: 'Datakomplethet',
                text: 'Endast godkända tidsposter och godkända utlägg inkluderas. Utkast och inlämnade-men-ej-godkända poster exkluderas för korrekt ekonomisk rapportering.',
            },
        ],
    },
    {
        // Lönespecifikationer ligger på Profil-sidan (ProfilePage.jsx). Det finns
        // ingen separat /dashboard/records-rutt.
        match: (p) => p.startsWith('/dashboard/profile'),
        title: 'Profil & lönespecifikationer',
        sections: [
            {
                heading: 'Lönespecifikationer',
                text: 'Lönespecifikationer genereras automatiskt när din tidrapport godkänns varje månad. Ladda ner valfri månads fil från listan på denna sida.',
            },
            {
                heading: 'Lönespecifikationshistorik',
                text: 'Den fullständiga listan visar alla månader för vilka en lönespecifikation har genererats. Lönespecifikationer lagras säkert och är tillgängliga oavsett anställningsstatus.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/integrations'),
        title: 'Integrationer',
        sections: [
            {
                heading: 'Fortnox',
                text: 'Anslut ditt Fortnox-konto för att synkronisera lönetransaktioner, frånvaro, utlägg och fakturaunderlag. Konfigurera lönekoder, frånvarokoder och kundmappningar i Admininställningar → Fortnox.',
            },
            {
                heading: 'E-post (Brevo)',
                text: 'Månadsvis påminnelsemejl och rapportleverans skickas via Brevo. Konfigurera API-nyckeln i Admininställningar → Notifikationer.',
            },
            {
                heading: 'Microsoft 365',
                text: 'SSO via Microsoft 365 tillåter anställda att logga in med sitt företagskonto. Uppgifter konfigureras på plattformsnivå — kontakta din administratör.',
            },
        ],
    },
    {
        match: (p) => p.startsWith('/dashboard/company'),
        title: 'Företagsinställningar',
        sections: [
            {
                heading: 'Företagsprofil',
                text: 'Besöksadress, momsregistreringsnummer, telefon och webbplats. Företagsnamn och organisationsnummer anges vid onboarding och kan inte ändras här.',
            },
            {
                heading: 'Varumärke',
                text: 'Ladda upp din företagslogotyp (visas i toppfältet) och en inloggningsbakgrundsbild. PWA-ikoner för hemskärmsinstallation kan också laddas upp här.',
            },
            {
                heading: 'Autentisering & åtkomst',
                text: 'Aktivera eller inaktivera e-post/lösenordsinloggning och Microsoft 365 SSO oberoende av varandra. Du kan begränsa inloggning till specifika e-postdomäner.',
            },
        ],
    },
];

/**
 * Returns help content for the given pathname and language, or null if no match.
 * @param {string} pathname - The current route pathname.
 * @param {string} [language='en'] - The active i18n language code.
 * @returns {{ title: string, sections: Array<{ heading: string, text: string }> } | null}
 */
export function getHelpContent(pathname, language = 'en') {
    const catalog = language.startsWith('sv') ? HELP_CONTENT_SV : HELP_CONTENT_EN;
    const entry = catalog.find((item) => item.match(pathname));
    if (!entry) return null;
    return { title: entry.title, sections: entry.sections };
}
